import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { authClient, hasVerifiedFactor } from '../../auth/client.ts'
import { Banner } from '../../ui/components.tsx'

/** The permissions whose commands or reads need a second factor (M23 item 32): every `core.command_type` with `requires_aal2`, plus the audited
 *  reads of pay rates and staff contacts (release b: the incident register's `incident.manage`, a machine's costs' `finance.manage` and its audited read's
 *  `finance.read`; the forecast's connector, `connector.manage`, left the list with the owner's decision of 2026-10-28: no code on that page). Whoever holds one is
 *  offered the enrolment right under their account card in Réglages, and each page
 *  of theirs says BEFORE its form when the factor is missing, instead of a refusal after submitting. The list mirrors the database's
 *  (`select distinct permission_code from core.command_type where requires_aal2`); Settings.test.tsx holds every code to the permission fixture. */
export const AAL2_PERMISSIONS: readonly string[] = [
  'asset.release', 'contact.manage', 'defect.downgrade', 'device.manage', 'finance.manage', 'finance.read', 'incident.manage', 'location.manage', 'member.manage',
  'pay.manage', 'pay.read', 'person.manage', 'qualification.override', 'retention.manage', 'review.flag_device',
]

/** Whether the login has a verified second factor: what the stored session says at once, what the server answers when it does (`null` until either
 *  says). The server's answer is the truth; the session's copy is refreshed by `authClient.user()` on every read. */
export function useVerifiedFactor(): boolean | null {
  const [verified, setVerified] = useState<boolean | null>(() => { const u = authClient.getSession()?.user; return u ? hasVerifiedFactor(u) : null })
  useEffect(() => {
    let live = true
    void authClient.user().then((u) => { if (live) setVerified(hasVerifiedFactor(u ?? authClient.getSession()?.user)) }).catch(() => undefined)
    return () => { live = false }
  }, [])
  return verified
}

/** The one-line warning a page that needs the second factor shows above its form when the login has none, with the way to Réglages (M23 item 32:
 *  the release form used to be filled in and then refused). Nothing while the state is not known yet. */
export function NoFactorBanner({ testId = 'no-factor' }: { testId?: string }) {
  const { t } = useTranslation()
  const verified = useVerifiedFactor()
  if (verified !== false) return null
  return (
    <Banner tone="warn" testId={testId}>
      <p className="font-semibold">{t('auth.mfaMissing')}</p>
      <Link to="/settings" className="btn mt-2 w-full" data-testid={`${testId}-go`}>{t('auth.mfaGo')}</Link>
    </Banner>
  )
}
